


/* TODO: Split this component into smaller components. */
import { Component, Vue, Watch } from "vue-property-decorator";

import { SelectionFilter, TextFilter } from "@Internal/Models";

import { Customers, Payments, Subscriptions } from "App";

import {
    Customer,
    CustomNotification,
    Field,
    Invoice,
    PagerSorter,
    PaymentMethod,
    Subscription
} from "App/Models";

import { Dictionary, GroupUser } from "App/Contracts";

import { EventBus, $t } from "App/IOC";

import { RecordActions, RecordAction } from "Web/Contracts";

import CustomerForm from "./Form.vue";

import Record from "Web/Components/Record.vue";
import Accordion from "Web/Components/Accordion.vue";
import Tab from "Web/Components/Tabs/Tab.vue";
import Tabs from "Web/Components/Tabs/Index.vue";
import LoadingDots from "Web/Components/Common/LoadingDots.vue";
import SubscriptionPlans from "Web/Components/Table/Fields/SubscriptionPlans.vue";
import QuotaLines from "Web/Views/Subscriptions/Single/QuotaLines.vue";

@Component({
    components: {
        Accordion,
        CustomerForm,
        Record,
        Tab,
        Tabs,
        LoadingDots,
        SubscriptionPlans,
        QuotaLines
    }
})
export default class SingleCustomerView extends Vue {
    private record: Customer = {
        Id: -1,
        Name: ""
    };

    private fields: Dictionary<Field> = {};

    private showScenarios: boolean = false;
    private editing: boolean = false;
    private loading: boolean = true;

    /* Lazyload des listes : on n'affiche qu'un lot de lignes, étendu au scroll. */
    private readonly lazyStep: number = 15;

    private invoices: Invoice[] = [];
    private invoicesLoading: boolean = true;
    private invoicesVisible: number = this.lazyStep;

    private payments: Invoice[] = [];
    private paymentsLoading: boolean = true;
    private paymentsVisible: number = this.lazyStep;

    /* Moyen de paiement enregistré du client (carte), affiché en tête de l'onglet Paiements.
       Initialisé à null (et non undefined) pour rester réactif via vue-class-component. */
    private paymentMethod: PaymentMethod | null = null;

    private subscriptions: Subscription[] = [];
    private subscriptionsLoading: boolean = true;
    private subscriptionsVisible: number = this.lazyStep;

    private groupUsers: GroupUser[] = [];
    private groupUsersLoading: boolean = true;

    private actions: RecordActions<Customer> = [
        [
            {
                Id: "Subscriptions",
                Title: $t( "CUSTOMERS_actionSubscriptions" ),
                Icon: "format_list_bulleted"
            },
            {
                Id: "GroupUsers",
                Title: $t( "CUSTOMERS_actionGroupUsers" ),
                Icon: "group"
            }
        ]
    ];

    private created() {
        this.setup();
    }

    private async setup() {
        this.fields = await Customers.Fields.Get();

        /* TODO: Handle invalid ID format. */
        await this.getCustomer( true );

        if( this.record.Name === undefined )
            return;

        document.title = this.record.Name;

        /* Sections indépendantes (chacune son propre loader) : on les charge en parallèle au lieu
           d'une cascade de 4 attentes série. Affichage ~÷4. */
        await Promise.all( [
            this.getInvoices(),
            this.getPayments(),
            this.getPaymentMethod(),
            this.getSubscriptions(),
            this.getGroupUsers()
        ] );
    }

    /* Utilisateurs du groupe rattaché au client (customer -> groupId -> users), même endpoint que
       la page Utilisateurs dédiée, affiché ici dans un onglet. */
    private async getGroupUsers() {
        this.groupUsersLoading = true;

        const [ users, error ] = await Customers.ListGroupUsers( this.record.Id );

        this.groupUsersLoading = false;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.groupUsers = users;
    }

    private groupUserStatusLabel( status: string ): string {
        switch( status ) {
            case "enabled":
                return this.$t( "CUSTOMERS_userStatusActive" ) as string;

            case "pending":
                return this.$t( "CUSTOMERS_userStatusProcessing" ) as string;

            case "disabled":
                return this.$t( "CUSTOMERS_userStatusInactive" ) as string;

            default:
                return "—";
        }
    }

    /* +33600000006 -> 06 00 00 00 06 (format FR local). */
    private formatPhone( phone: string ): string {
        if( !phone )
            return "—";

        let digits = phone.replace( /[^\d+]/g, "" );

        if( digits.indexOf( "+33" ) === 0 )
            digits = "0" + digits.slice( 3 );
        else if( digits.indexOf( "0033" ) === 0 )
            digits = "0" + digits.slice( 4 );

        digits = digits.replace( /\D/g, "" );

        if( digits.length !== 10 )
            return phone;

        return ( digits.match( /.{2}/g ) || [] ).join( " " );
    }

    /* Actifs d'abord, puis en attente, puis inactifs. */
    private get sortedGroupUsers(): GroupUser[] {
        const rank: { [ key: string ]: number } = {
            enabled: 0,
            pending: 1,
            disabled: 2
        };

        return this.groupUsers.slice().sort(
            ( first: GroupUser, second: GroupUser ): number => {
                const firstRank = rank[ first.Status ] !== undefined ? rank[ first.Status ] : 3;
                const secondRank = rank[ second.Status ] !== undefined ? rank[ second.Status ] : 3;

                return firstRank - secondRank;
            }
        );
    }

    /* Le contact propriétaire du compte = celui dont l'email correspond à l'email du compte. */
    private isAccountOwner( user: GroupUser ): boolean {
        if( this.record === undefined )
            return false;

        const accountEmail = ( this.record.Email || "" ).toLowerCase();
        if( accountEmail === "" )
            return false;

        return ( user.Email || "" ).toLowerCase() === accountEmail;
    }

    /* Abonnements du client (actifs, en essai, résiliés…) lus en direct via le même endpoint que la
       liste Abonnements, scopé sur le client courant par le filtre "Customer". */
    private async getSubscriptions() {
        this.subscriptionsLoading = true;

        const customerFilter = new SelectionFilter( "Customer", "", [], "" );
        customerFilter.Value = [ this.record ];

        /* Tri du plus récent au plus ancien (CreatedAt décroissant), comme la liste Abonnements. */
        const pagerSorter = PagerSorter.ForColumn(
            Subscriptions.Columns.GetDefaultSort(),
            undefined,
            true
        );
        pagerSorter.Limit = 200;

        const [ subscriptions, , error ] = await Subscriptions.Find(
            [ customerFilter ], pagerSorter
        );

        this.subscriptionsLoading = false;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.subscriptions = subscriptions;
        this.subscriptionsVisible = this.lazyStep;
    }

    /* L'ONGLET « QUOTAS » ( Fred, 08/10/2026 ) — l'abonnement en cours ; a
       defaut, le DERNIER QUI A TOURNE ( « reprendre le dernier abonnement
       actif » ) : resilie, impaye, en retard ou expire. Pas un devis ni un essai
       plus recent, qui n'ont jamais facture ces quotas. La liste est triee du
       plus recent au plus ancien. */
    private get quotaSubscription(): Subscription | undefined {
        const active = this.subscriptions.find(
            ( subscription: Subscription ): boolean => subscription.Status === "Active"
        );

        if( active !== undefined )
            return active;

        const ran = [ "Canceled", "Unpaid", "PastDue", "Expired" ];
        const lastRun = this.subscriptions.find(
            ( subscription: Subscription ): boolean =>
                ran.indexOf( String( subscription.Status ) ) !== -1
        );

        return lastRun !== undefined ? lastRun : this.subscriptions[ 0 ];
    }

    private get visibleSubscriptions(): Subscription[] {
        return this.subscriptions.slice( 0, this.subscriptionsVisible );
    }

    private onSubscriptionsScroll( event: Event ): void {
        if( this.nearBottom( event ) &&
            this.subscriptionsVisible < this.subscriptions.length
        )
            this.subscriptionsVisible += this.lazyStep;
    }

    private subscriptionStatusLabel( subscription: Subscription ): string {
        switch( subscription.Status ) {
            case "Active":
                return $t( "SUBSCRIPTIONS_statusActive" );

            case "Trial":
                return $t( "SUBSCRIPTIONS_statusTrial" );

            case "Offer":
                return $t( "SUBSCRIPTIONS_statusOffer" );

            case "Unpaid":
                return $t( "SUBSCRIPTIONS_statusUnpaid" );

            case "PastDue":
                return $t( "SUBSCRIPTIONS_statusPastDue" );

            case "Incomplete":
                return $t( "SUBSCRIPTIONS_statusIncomplete" );

            case "Expired":
                return $t( "SUBSCRIPTIONS_statusIncompleted_Expired" );

            case "Canceled":
                return $t( "SUBSCRIPTIONS_statusCanceled" );

            default:
                return "—";
        }
    }

    /* Récurrence de facturation (Mensuel / Annuel), libellée comme la fiche abonnement. */
    private subscriptionRecurrenceLabel( subscription: Subscription ): string {
        const recurrence = ( subscription as any ).Recurrence;

        if( recurrence === undefined )
            return "—";

        return recurrence === "Yearly"
            ? $t( "SUBSCRIPTIONS_recurrenceYearly" )
            : $t( "SUBSCRIPTIONS_recurrenceMonthly" );
    }

    /* Date de dernière modification (UpdatedAt exposé par le backend, formaté comme CreatedAt).
       Tiret tant que le backend déployé ne renvoie pas encore le champ. */
    private subscriptionUpdatedAt( subscription: Subscription ): string {
        const updatedAt = ( subscription as any ).UpdatedAt;

        return ( updatedAt !== undefined && updatedAt !== null && updatedAt !== "" )
            ? String( updatedAt )
            : "—";
    }

    /* Totaux récurrents recalculés depuis les lignes (le payload de liste ne porte pas de total
       agrégé), exprimés en mensuel/annuel moyen — même logique que la liste Abonnements. */
    private subscriptionHasTotal( subscription: Subscription ): boolean {
        return ( ( subscription as any ).Lines || [] ).length > 0;
    }

    private lineAmount( line: any ): number {
        const subtotal = line.Subtotal;

        if( subtotal === undefined || subtotal === null )
            return 0;

        if( typeof subtotal === "number" )
            return subtotal;

        if( typeof subtotal.ToFixed === "function" )
            return Number( subtotal.ToFixed( 2 ) );

        return Number( subtotal ) || 0;
    }

    private recurrentTotal( subscription: Subscription ): number {
        const lines = ( subscription as any ).Lines || [];
        let total = 0;

        for( const line of lines ) {
            if( line.Price === undefined || line.Price.Recurrence === undefined )
                continue;

            total += this.lineAmount( line );
        }

        return total;
    }

    private monthlyTotal( subscription: Subscription ): number {
        const total = this.recurrentTotal( subscription );

        return ( subscription as any ).Recurrence === "Yearly" ? total / 12 : total;
    }

    private yearlyTotal( subscription: Subscription ): number {
        return this.monthlyTotal( subscription ) * 12;
    }

    private formatAmount( value: number ): string {
        return value.toLocaleString( "fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    /* Paiements du client lus depuis le mirror local des factures (table Invoice alimentée par le
       webhook + backfill Stripe). On scope côté serveur sur l'e-mail exact du client : le mirror
       stocke toujours CustomerEmail, alors que la FK Customer interne n'est posée que lorsque le
       client Stripe a pu être rattaché. À la différence de l'onglet Factures (Stripe en direct),
       cette liste reste disponible même si Stripe est injoignable. */
    private async getPayments() {
        this.paymentsLoading = true;

        const email = this.record.Email || "";

        /* Sans e-mail on ne peut pas scoper le mirror : on évite de retourner tous
           les paiements. */
        if( email === "" ) {
            this.payments = [];
            this.paymentsLoading = false;
            return;
        }

        const emailFilter = new TextFilter( "Email", "" );
        emailFilter.Value = [ email ];

        const pagerSorter = new PagerSorter();
        pagerSorter.Limit = 200;

        const [ payments, , error ] = await Payments.Find( [ emailFilter ], pagerSorter );

        this.paymentsLoading = false;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.payments = payments;
        this.paymentsVisible = this.lazyStep;
    }

    /* Carte enregistrée du client (mirror local, pas d'appel Stripe). Silencieux si absent : on
       affiche simplement la liste des paiements sans en-tête de carte. */
    private async getPaymentMethod() {
        const [ paymentMethod ] = await Customers.GetPaymentMethod( this.record.Id );

        /* En cas d'erreur (ex. endpoint pas encore déployé → 404), on dégrade silencieusement :
           l'onglet Paiements s'affiche sans en-tête de carte, sans toast d'erreur. */
        this.paymentMethod = paymentMethod === undefined ? null : paymentMethod;
    }

    /* "visa" -> "Visa", "sepa" -> "SEPA". */
    private paymentMethodBrandLabel( brand?: string ): string {
        if( brand === undefined || brand === "" )
            return "—";

        if( brand.toLowerCase() === "sepa" )
            return "SEPA";

        return brand.charAt( 0 ).toUpperCase() + brand.slice( 1 );
    }

    /* Show the SEPA mandate download link (Stripe-hosted mandate PDF) only for a SEPA payment
       method that carries a mandate URL. Card payments have no SEPA mandate. */
    private hasSepaMandate(): boolean {
        if( this.paymentMethod === null )
            return false;

        const brand = ( this.paymentMethod.Brand || "" ).toLowerCase();

        return brand === "sepa" && Boolean( this.paymentMethod.MandateUrl );
    }

    private get visibleInvoices(): Invoice[] {
        return this.invoices.slice( 0, this.invoicesVisible );
    }

    private get visiblePayments(): Invoice[] {
        return this.payments.slice( 0, this.paymentsVisible );
    }

    /* Étend le lot affiché quand le scroll approche du bas du conteneur (lazyload). */
    private onInvoicesScroll( event: Event ): void {
        if( this.nearBottom( event ) && this.invoicesVisible < this.invoices.length )
            this.invoicesVisible += this.lazyStep;
    }

    private onPaymentsScroll( event: Event ): void {
        if( this.nearBottom( event ) && this.paymentsVisible < this.payments.length )
            this.paymentsVisible += this.lazyStep;
    }

    private nearBottom( event: Event ): boolean {
        const element = event.target as HTMLElement;

        return ( element.scrollTop + element.clientHeight ) >= ( element.scrollHeight - 80 );
    }

    /* Load the full billing history of the customer (every invoice across all of his
       subscriptions, current and previously canceled ones). */
    private async getInvoices() {
        this.invoicesLoading = true;

        const [ invoices, error ] = await Customers.ListInvoices( this.record.Id );

        this.invoicesLoading = false;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.invoices = invoices;
        this.invoicesVisible = this.lazyStep;
    }

    private getInvoiceStatus( status: string ): string {
        switch( true ) {
            case status === "Paid":
                return $t( "SUBSCRIPTION_paid" );

            case status === "Open":
                return $t( "SUBSCRIPTION_open" );

            case status === "Uncollectible":
                return $t( "SUBSCRIPTION_uncollectible" );

            case status === "Rejected":
                return $t( "CUSTOMERS_invoiceRejected" );

            default:
                return $t( "SUBSCRIPTION_void" );
        }
    }

    private downloadInvoice( invoice: Invoice ) {
        if( invoice.DownloadURL === undefined )
            return;

        window.location.href = invoice.DownloadURL;
    }

    /* En-tête : facture la plus récente du client (par date de création), pour afficher son dernier
       statut de paiement en badge à côté du nom. */
    private get latestInvoice(): Invoice | undefined {
        let latest: Invoice | undefined;

        for( const invoice of this.invoices ) {
            if( invoice.CreatedAt === undefined )
                continue;

            if( latest === undefined
                || latest.CreatedAt === undefined
                || invoice.CreatedAt.ToTimestamp() > latest.CreatedAt.ToTimestamp() )
                latest = invoice;
        }

        return latest;
    }

    /* Statut Stripe brut de la dernière facture (modificateur de classe -> couleur du badge). */
    private get lastPaymentStatus(): string {
        const invoice = this.latestInvoice;

        return invoice !== undefined && invoice.Status !== undefined ? invoice.Status : "";
    }

    /* Libellé traduit du dernier statut de paiement (mêmes libellés que l'onglet Factures).
       Brouillon (Draft) ou aucune facture -> vide, pas de badge. */
    private get lastPaymentStatusLabel(): string {
        const status = this.lastPaymentStatus;

        if( status === "" || status === "Draft" )
            return "";

        return this.getInvoiceStatus( status );
    }

    /* Reset data on Product change*/
    private resetData() {
        this.fields = {};
        this.record = {
            Id: -1,
            Name: ""
        };
    }

    /* Refresh data if navigated to a different Product
        diractly from the current page. */
    @Watch( "$route" )
    private onRouteParametersChange( from: any, to: any ) {
        if( to.name !== "customer" || from.params.id === to.params.id )
            return;
        this.resetData();
        this.setup();
    }

    private async save( record: Customer ) {
        if( this.record === undefined || record === undefined )
            return;

        const [ updatedRecord, error ] =
            await Customers.Update( this.record, record );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.record = updatedRecord as Customer;

        this.showUpdateNotification();
    }


    private async getCustomer( silent: boolean = false ) {
        const [ record, error ] = await Customers.Get( Number( this.$route.params.id ) );

        if( error !== undefined || record === undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        /* Get plans. */
        const filter = new TextFilter( "CustomerId", "" );
        filter.Value = [ record.Id ];

        this.record = record as Customer;

        if( !silent )
            this.showUpdateNotification();
    }

    private showUpdateNotification() {
        EventBus.Publish( "Notification", CustomNotification.Success(
            this.$t( "GLOBAL_CUSTOMER" ).toString(), this.$t( "CUSTOMERS_updated" ).toString()
        ));
    }

    private underConstruction() {
        EventBus.Publish( "UnderConstruction" );
    }

    private update( customer: Customer, silent: boolean = false ) {
        this.record = customer;

        if( !silent )
            this.showUpdateNotification();
    }

    private openSubscription( subscription: Subscription ): void {
        this.$router.push( `/subscriptions/${subscription.Id}` );
    }

    private execute( action: RecordAction ): void {
        switch( action.Id ) {
            case "Subscriptions":
                this.$router.push( `/subscriptions?customer=${this.record.Id}` );
                break;

            case "GroupUsers":
                this.$router.push( `/customers/${this.record.Id}/users` );
                break;

            default:
                EventBus.Publish( "UnderConstruction" );
                break;
        }
    }

    @Watch( "record" )
    private onRecordChange( record?: Customer ) {
        if( record === undefined )
            return;

        setTimeout( () => this.loading = record.Name === undefined, 200 );
    }
}
